/*
 * Cut To Length Optimiser
 * Ported from the Drillcut Tools Hub for the BSP Smart Tools Hub.
 *
 * Design: BSP tokens — #1B449C blue, Barlow/Barlow Condensed, JetBrains Mono.
 * All measurements in metric (mm). Kerf-aware FFD algorithm, pure client-side.
 *
 * Modes: Standard (single profile) and Multi-Profile with grouped results.
 * CSV import, quantity multiplier, and branded PDF export.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { Upload, Info, Scissors, Download, Trash2, Zap } from "lucide-react";
import Papa from "papaparse";
import { useAuth } from "../../../auth/useAuth";
import { InstructionsPanel } from "./InstructionsPanel";
import {
  CATEGORIES,
  PROFILES,
  type CutDef,
  type CutPiece,
  type CutRow,
  type Mode,
  type MultiCutRow,
  type MultiProfileResults,
  type Results,
  type SectionResult,
  type StockBar,
  colorFor,
  consolidatePatterns,
  firstFitDecreasing,
  lightColorFor,
} from "./lib/ctlOptimiser";
import { exportCtlPdf } from "./lib/exportPdf";

// Kerf swatch colour (screen) — light BSP blue, matches the PDF export tint
const KERF_COLOR = "#B9C9E6";

// ── Responsive helper ────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${breakpoint}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Local UI primitives (BSP-styled) ─────────────────────────────────────────

function Card({
  title,
  actions,
  style,
  children,
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div className="bsp-card" style={style}>
      {(title || actions) && (
        <div className="bsp-card-title-row">
          {title && <span className="bsp-eyebrow">{title}</span>}
          <span
            style={{ flex: 1, height: 1, background: "var(--bsp-border)" }}
          />
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="bsp-label">{children}</div>;
}

function SegmentedToggle<T extends string>({
  items,
  active,
  onChange,
  "aria-label": ariaLabel,
}: {
  items: { id: T; label: string }[];
  active: T;
  onChange: (id: T) => void;
  "aria-label"?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        background: "var(--bsp-blue-pale)",
        border: "1px solid var(--bsp-blue-border)",
        borderRadius: 4,
        padding: 2,
        gap: 2,
      }}
    >
      {items.map(i => {
        const isActive = active === i.id;
        return (
          <button
            key={i.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(i.id)}
            style={{
              height: 26,
              padding: "0 12px",
              border: "none",
              borderRadius: 3,
              cursor: "pointer",
              font: "700 11px/1 var(--bsp-font-sans)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              background: isActive ? "var(--bsp-blue)" : "transparent",
              color: isActive ? "#fff" : "var(--bsp-fg-muted)",
              transition: "background 120ms ease, color 120ms ease",
            }}
          >
            {i.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Cross-section Icons ──────────────────────────────────────────────────────

function ProfileIcon({
  category,
  size = 40,
}: {
  category: string;
  size?: number;
}) {
  const sz = size * 0.7;
  const off = (size - sz) / 2;

  if (category === "Mega") {
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <rect
          x={off}
          y={off}
          width={sz}
          height={sz}
          rx={2}
          fill="#1B449C"
          fillOpacity={0.18}
          stroke="#1B449C"
          strokeWidth={1.5}
        />
        <rect
          x={off + 4}
          y={off + 4}
          width={sz - 8}
          height={sz - 8}
          rx={1}
          fill="#F8F9F8"
          stroke="#2453B5"
          strokeWidth={0.8}
        />
      </svg>
    );
  }

  if (category === "Strut") {
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <path
          d="M8 8 L8 32 L32 32 L32 8 L28 8 L28 10 C28 11.5 27 12.5 25.5 13 L25 13 L25 28 L15 28 L15 13 L14.5 13 C13 12.5 12 11.5 12 10 L12 8 Z"
          fill="#1B449C"
          fillOpacity={0.18}
          stroke="#1B449C"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (category === "Angle") {
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <path
          d="M10 8 L10 32 L32 32 L32 27 L15 27 L15 8 Z"
          fill="#1B449C"
          fillOpacity={0.18}
          stroke="#1B449C"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (category === "Allthread") {
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={12}
          fill="#1B449C"
          fillOpacity={0.18}
          stroke="#1B449C"
          strokeWidth={1.5}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={5}
          fill="#F8F9F8"
          stroke="#2453B5"
          strokeWidth={0.8}
        />
      </svg>
    );
  }

  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: string;
  variant?: "default" | "good" | "warn" | "bad";
}) {
  const colors = {
    default: "var(--bsp-fg)",
    good: "var(--bsp-green-fg)",
    warn: "var(--bsp-orange-fg)",
    bad: "var(--bsp-red-fg)",
  };
  return (
    <div
      style={{
        background: "var(--bsp-blue-pale)",
        borderRadius: 6,
        padding: "10px 12px",
        border: "1px solid var(--bsp-blue-border)",
      }}
    >
      <div
        style={{
          fontSize: 9,
          color: "var(--bsp-fg-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.09em",
          marginBottom: 4,
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        className="bsp-mono"
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: colors[variant],
          letterSpacing: "-0.5px",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function BarLayout({
  stocks,
  stockLen,
  kerf,
  uniqueLens,
}: {
  stocks: StockBar[];
  stockLen: number;
  kerf: number;
  uniqueLens: number[];
}) {
  // Consolidate identical stock patterns
  const consolidated = consolidatePatterns(stocks);

  return (
    <div>
      {consolidated.map((group, gi) => {
        const s = group.stock;
        const usedPieces = s.cuts.reduce((a, c) => a + c.len, 0);
        const wasteLen = stockLen - usedPieces - s.cuts.length * kerf;
        const wastePct = (wasteLen / stockLen) * 100;

        return (
          <div key={gi} style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--bsp-blue)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Pattern {gi + 1}
                {group.count > 1 ? (
                  <span
                    style={{
                      fontWeight: 600,
                      color: "var(--bsp-blue-2)",
                      marginLeft: 6,
                      fontSize: 10,
                    }}
                  >
                    (x{group.count} lengths)
                  </span>
                ) : (
                  ""
                )}
              </span>
              <span
                className="bsp-mono"
                style={{ fontSize: 11, color: "var(--bsp-fg-subtle)" }}
              >
                {s.cuts.length} pcs · {wasteLen.toFixed(0)}mm waste (
                {wastePct.toFixed(1)}%)
              </span>
            </div>
            <div
              style={{
                height: 36,
                borderRadius: 4,
                background: "var(--bsp-blue-pale)",
                overflow: "hidden",
                display: "flex",
                border: "1px solid var(--bsp-blue-border)",
                width: "100%",
              }}
            >
              {s.cuts.map((c, ci) => {
                const pct = (c.len / stockLen) * 100;
                const kpct = kerf > 0 ? (kerf / stockLen) * 100 : 0;
                return (
                  <React.Fragment key={ci}>
                    <div
                      title={`${c.len}mm`}
                      className="bsp-mono"
                      style={{
                        flex: `0 0 ${pct}%`,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 600,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        background: lightColorFor(c.len, uniqueLens),
                        borderRight: `2px solid ${colorFor(c.len, uniqueLens)}`,
                        color: colorFor(c.len, uniqueLens),
                      }}
                    >
                      {pct > 5 ? c.len : ""}
                    </div>
                    {kerf > 0 && kpct > 0.3 && (
                      <div
                        style={{
                          flex: `0 0 ${kpct}%`,
                          height: "100%",
                          background: KERF_COLOR,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {wastePct > 0.2 && (
                <div
                  style={{
                    flex: `0 0 ${wastePct}%`,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    color: "var(--bsp-fg-subtle)",
                    background:
                      "repeating-linear-gradient(-45deg, var(--bsp-blue-border) 0, var(--bsp-blue-border) 3px, var(--bsp-blue-pale) 3px, var(--bsp-blue-pale) 8px)",
                  }}
                >
                  {wastePct > 5 ? `${Math.round(wasteLen)}mm` : ""}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {uniqueLens.map(l => (
          <div
            key={l}
            className="bsp-mono"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              color: "var(--bsp-fg-muted)",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: colorFor(l, uniqueLens),
                flexShrink: 0,
              }}
            />
            {l}mm
          </div>
        ))}
        {kerf > 0 && (
          <div
            className="bsp-mono"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              color: "var(--bsp-fg-muted)",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: KERF_COLOR,
                flexShrink: 0,
              }}
            />
            Kerf ({kerf}mm)
          </div>
        )}
        <div
          className="bsp-mono"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11,
            color: "var(--bsp-fg-muted)",
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background:
                "repeating-linear-gradient(-45deg, var(--bsp-border-strong) 0, var(--bsp-border-strong) 2px, var(--bsp-blue-pale) 2px, var(--bsp-blue-pale) 6px)",
              flexShrink: 0,
            }}
          />
          Waste
        </div>
      </div>
    </div>
  );
}

// ── Material Requirements Summary ────────────────────────────────────────────

function MaterialSummaryTable({ sections }: { sections: SectionResult[] }) {
  const grandTotalLengths = sections.reduce((a, s) => a + s.stocks.length, 0);
  const grandTotalWaste = sections.reduce((a, s) => a + s.totalWaste, 0);
  const grandTotalStock = sections.reduce((a, s) => a + s.totalStock, 0);
  const grandTotalPiece = sections.reduce((a, s) => a + s.totalPiece, 0);
  const grandAvgUtil =
    grandTotalStock > 0 ? (grandTotalPiece / grandTotalStock) * 100 : 0;
  const yieldColor = (pct: number) =>
    pct >= 90
      ? "var(--bsp-green-fg)"
      : pct >= 75
        ? "var(--bsp-orange-fg)"
        : "var(--bsp-red-fg)";
  const totalCell: React.CSSProperties = {
    color: "var(--bsp-blue)",
    fontWeight: 700,
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="bsp-table">
        <thead>
          <tr>
            {[
              "Section",
              "No. Lengths Required",
              "Total Waste (mm)",
              "Avg Waste / Length (mm)",
              "Avg Utilisation (%)",
            ].map(h => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((sec, i) => {
            const avgWastePerLength =
              sec.stocks.length > 0 ? sec.totalWaste / sec.stocks.length : 0;
            return (
              <tr key={i}>
                <td>{sec.profileName}</td>
                <td className="bsp-mono">{sec.stocks.length}</td>
                <td className="bsp-mono">{sec.totalWaste.toFixed(0)}</td>
                <td className="bsp-mono">{avgWastePerLength.toFixed(0)}</td>
                <td
                  className="bsp-mono"
                  style={{ color: yieldColor(sec.yieldPct) }}
                >
                  {sec.yieldPct.toFixed(1)}%
                </td>
              </tr>
            );
          })}
          {/* Totals row */}
          <tr style={{ background: "var(--bsp-blue-tint)" }}>
            <td style={totalCell}>TOTAL</td>
            <td className="bsp-mono" style={totalCell}>
              {grandTotalLengths}
            </td>
            <td className="bsp-mono" style={totalCell}>
              {grandTotalWaste.toFixed(0)}
            </td>
            <td className="bsp-mono" style={totalCell}>
              {grandTotalLengths > 0
                ? (grandTotalWaste / grandTotalLengths).toFixed(0)
                : "0"}
            </td>
            <td className="bsp-mono" style={totalCell}>
              {grandAvgUtil.toFixed(1)}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function CutToLengthPage() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  // Mode
  const [mode, setMode] = useState<Mode>("standard");

  // Extended job/project details
  const [projectNumber, setProjectNumber] = useState("");
  const [projectName, setProjectName] = useState("");
  const [jobName, setJobName] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [client, setClient] = useState("");
  const [notes, setNotes] = useState("");
  const [jobRef, setJobRef] = useState("");
  const [jobDate, setJobDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [revision, setRevision] = useState("");
  const [createdBy, setCreatedBy] = useState("");

  // Default "Created By" to logged-in user's name
  useEffect(() => {
    if (user?.displayName && !createdBy) setCreatedBy(user.displayName);
  }, [user?.displayName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Material (standard mode)
  const [stockLen, setStockLen] = useState("6000");
  const [kerf, setKerf] = useState("3");
  const [matDesc, setMatDesc] = useState("");
  const [profileCode, setProfileCode] = useState("");

  // Multiplier
  const [multiplier, setMultiplier] = useState("1");

  // Cut list (standard mode)
  const [cuts, setCuts] = useState<CutRow[]>([
    { id: 1, length: "1200", qty: "4" },
    { id: 2, length: "850", qty: "6" },
    { id: 3, length: "500", qty: "3" },
  ]);
  const [nextId, setNextId] = useState(4);

  // Cut list (multi-profile mode)
  const [multiCuts, setMultiCuts] = useState<MultiCutRow[]>([
    { id: 1, section: "", length: "", qty: "1" },
  ]);
  const [multiNextId, setMultiNextId] = useState(2);
  const [lastSection, setLastSection] = useState("");

  // Results
  const [results, setResults] = useState<Results | null>(null);
  const [multiResults, setMultiResults] = useState<MultiProfileResults | null>(
    null
  );
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showCsvTooltip, setShowCsvTooltip] = useState(false);

  // Refs
  const resultsRef = useRef<HTMLDivElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Hydration hook: another tool can hand a cut list over via sessionStorage
  // (same contract as the Drillcut hub's Cost Estimator integration).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("costEstimator_cutList");
      if (!raw) return;
      sessionStorage.removeItem("costEstimator_cutList");
      const data = JSON.parse(raw) as {
        stockCode: string;
        description: string;
        fullLength: number;
        category: string;
        cutList: Array<{ length: number; qty: number }>;
        projectDetails?: {
          projectNumber: string;
          projectName: string;
          jobName: string;
          client: string;
          projectAddress: string;
          jobRef: string;
          jobDate: string;
          revision: string;
          createdBy: string;
          projectNotes: string;
        };
      };

      const matchedProfile = PROFILES.find(p => p.code === data.stockCode);
      if (matchedProfile) {
        setProfileCode(matchedProfile.code);
        setStockLen(String(matchedProfile.stockLength));
        setMatDesc(matchedProfile.label);
        setKerf(matchedProfile.category === "Allthread" ? "0" : "3");
      } else {
        setMatDesc(data.description);
        if (data.fullLength > 0) setStockLen(String(data.fullLength));
        const catLower = data.category?.toLowerCase() || "";
        setKerf(catLower === "allthread" ? "0" : "3");
      }

      if (data.cutList && data.cutList.length > 0) {
        let id = 1;
        const newCuts: CutRow[] = data.cutList.map(item => ({
          id: id++,
          length: String(item.length),
          qty: String(item.qty),
        }));
        setCuts(newCuts);
        setNextId(id);
      }

      if (data.projectDetails) {
        const pd = data.projectDetails;
        if (pd.projectNumber) setProjectNumber(pd.projectNumber);
        if (pd.projectName) setProjectName(pd.projectName);
        if (pd.jobName) setJobName(pd.jobName);
        if (pd.client) setClient(pd.client);
        if (pd.projectAddress) setProjectAddress(pd.projectAddress);
        if (pd.jobRef) setJobRef(pd.jobRef);
        if (pd.jobDate) setJobDate(pd.jobDate);
        if (pd.revision) setRevision(pd.revision);
        if (pd.createdBy) setCreatedBy(pd.createdBy);
        if (pd.projectNotes) setNotes(pd.projectNotes);
      }
    } catch (e) {
      console.warn("Failed to hydrate cut list from sessionStorage:", e);
    }
  }, []);

  // Close tooltip on click outside
  useEffect(() => {
    if (!showCsvTooltip) return;
    const handler = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowCsvTooltip(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCsvTooltip]);

  // ── Standard Mode Handlers ─────────────────────────────────────────────────

  const addRow = () => {
    setNextId(n => {
      setCuts(prev => [...prev, { id: n, length: "", qty: "1" }]);
      return n + 1;
    });
  };

  const removeRow = (id: number) => {
    if (cuts.length <= 1) return;
    setCuts(prev => prev.filter(r => r.id !== id));
  };

  const updateRow = (id: number, field: "length" | "qty", value: string) => {
    setCuts(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const onProfileChange = (code: string) => {
    setProfileCode(code);
    const profile = PROFILES.find(p => p.code === code);
    if (profile) {
      setMatDesc(profile.label);
      setStockLen(String(profile.stockLength));
      if (profile.category === "Allthread") {
        setKerf("0");
      } else {
        setKerf("3");
      }
    }
  };

  // ── Multi-Profile Mode Handlers ────────────────────────────────────────────

  const addMultiRow = () => {
    setMultiNextId(n => {
      setMultiCuts(prev => [
        ...prev,
        { id: n, section: lastSection, length: "", qty: "1" },
      ]);
      return n + 1;
    });
  };

  const removeMultiRow = (id: number) => {
    if (multiCuts.length <= 1) return;
    setMultiCuts(prev => prev.filter(r => r.id !== id));
  };

  const updateMultiRow = (
    id: number,
    field: "section" | "length" | "qty",
    value: string
  ) => {
    setMultiCuts(prev =>
      prev.map(r => (r.id === id ? { ...r, [field]: value } : r))
    );
    if (field === "section" && value) {
      setLastSection(value);
    }
  };

  // ── CSV Upload ─────────────────────────────────────────────────────────────

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: result => {
        const rows = result.data as string[][];
        if (!rows.length) {
          setError("CSV file is empty.");
          return;
        }

        // Skip the first row if it looks like a header (numeric column is not a number)
        let startIdx = 0;
        if (rows.length > 1) {
          const secondCol = rows[0]?.[mode === "multi" ? 1 : 0]?.trim();
          if (secondCol && isNaN(parseFloat(secondCol))) startIdx = 1;
        }

        if (mode === "multi") {
          // Positional: col0 = Section code, col1 = Length, col2 = Qty
          const newCuts: MultiCutRow[] = [];
          let id = multiNextId;
          const errors: string[] = [];

          for (let i = startIdx; i < rows.length; i++) {
            const cols = rows[i];
            if (!cols || cols.length < 3) continue;
            const sectionVal = (cols[0] ?? "").trim();
            const lengthVal = (cols[1] ?? "").trim();
            const qtyVal = (cols[2] ?? "").trim();

            if (lengthVal && qtyVal) {
              const l = parseFloat(lengthVal);
              const q = parseInt(qtyVal);
              if (!isNaN(l) && l > 0 && !isNaN(q) && q > 0) {
                const matchedProfile = PROFILES.find(p => p.code === sectionVal);
                if (sectionVal && !matchedProfile) {
                  errors.push(`Unknown stock code: "${sectionVal}"`);
                }
                newCuts.push({
                  id,
                  section: matchedProfile ? sectionVal : "",
                  length: String(l),
                  qty: String(q),
                });
                id++;
              }
            }
          }

          if (newCuts.length > 0) {
            setMultiCuts(newCuts);
            setMultiNextId(id);
            setMultiResults(null);
            if (errors.length > 0) {
              setError(
                `Imported ${newCuts.length} rows. ${errors.length} rows had unrecognised stock codes — please assign sections manually.`
              );
            } else {
              setError("");
            }
          } else {
            setError(
              "No valid rows found in CSV. Expected 3 columns: Section, Length, Qty"
            );
          }
        } else {
          // Positional: col0 = Length, col1 = Qty
          const newCuts: CutRow[] = [];
          let id = nextId;

          for (let i = startIdx; i < rows.length; i++) {
            const cols = rows[i];
            if (!cols || cols.length < 2) continue;
            const lengthVal = (cols[0] ?? "").trim();
            const qtyVal = (cols[1] ?? "").trim();

            if (lengthVal && qtyVal) {
              const l = parseFloat(lengthVal);
              const q = parseInt(qtyVal);
              if (!isNaN(l) && l > 0 && !isNaN(q) && q > 0) {
                newCuts.push({ id, length: String(l), qty: String(q) });
                id++;
              }
            }
          }

          if (newCuts.length > 0) {
            setCuts(newCuts);
            setNextId(id);
            setResults(null);
            setError("");
          } else {
            setError(
              "No valid rows found in CSV. Expected 2 columns: Length, Qty"
            );
          }
        }
      },
      error: () => {
        setError("Failed to parse CSV file.");
      },
    });

    e.target.value = "";
  };

  // ── Calculate (Standard) ───────────────────────────────────────────────────

  const calculateStandard = useCallback(() => {
    setError("");
    const sLen = parseFloat(stockLen) || 0;
    const kerfVal = parseFloat(kerf) || 0;
    const mult = Math.max(1, parseInt(multiplier) || 1);
    if (sLen <= 0) {
      setError("Enter a valid stock length.");
      return;
    }

    const pieces: CutPiece[] = [];
    const cutDefs: CutDef[] = [];

    for (const row of cuts) {
      const l = parseFloat(row.length) || 0;
      const rawQ = parseInt(row.qty) || 0;
      if (l <= 0 || rawQ <= 0) continue;
      if (l > sLen) {
        setError(`Cut length ${l}mm exceeds stock length ${sLen}mm.`);
        return;
      }
      const q = rawQ * mult; // Apply multiplier to quantities
      cutDefs.push({ len: l, qty: q });
      for (let j = 0; j < q; j++)
        pieces.push({ len: l, defIdx: cutDefs.length - 1 });
    }

    if (!pieces.length) {
      setError("Enter at least one valid piece.");
      return;
    }

    const stocks = firstFitDecreasing(pieces, sLen, kerfVal);
    if (!stocks) {
      setError("A piece exceeds the stock length.");
      return;
    }

    const totalPiece = pieces.reduce((a, p) => a + p.len, 0);
    const totalStock = stocks.length * sLen;
    const totalKerfs = stocks.reduce((a, s) => a + s.cuts.length * kerfVal, 0);
    const totalWaste = totalStock - totalPiece - totalKerfs;
    const yieldPct = (totalPiece / totalStock) * 100;

    const profile = PROFILES.find(p => p.code === profileCode);
    const profileLabel = profile ? profile.label : "";
    const pCode = profile ? profile.code : "";

    setResults({
      stocks,
      cutDefs,
      totalPiece,
      totalStock,
      totalKerfs,
      totalWaste,
      yieldPct,
      stockLen: sLen,
      kerf: kerfVal,
      matDesc,
      profileLabel,
      profileCode: pCode,
    });
    setMultiResults(null);
  }, [cuts, stockLen, kerf, matDesc, profileCode, multiplier]);

  // ── Calculate (Multi-Profile) ──────────────────────────────────────────────

  const calculateMulti = useCallback(() => {
    setError("");
    const kerfVal = parseFloat(kerf) || 0;
    const mult = Math.max(1, parseInt(multiplier) || 1);

    // Group cuts by section
    const sectionMap = new Map<
      string,
      { profile: (typeof PROFILES)[number]; rows: MultiCutRow[] }
    >();
    const unassigned: number[] = [];

    for (let i = 0; i < multiCuts.length; i++) {
      const row = multiCuts[i]!;
      const l = parseFloat(row.length) || 0;
      const q = parseInt(row.qty) || 0;
      if (l <= 0 || q <= 0) continue;

      if (!row.section) {
        unassigned.push(i + 1);
        continue;
      }

      const profile = PROFILES.find(p => p.code === row.section);
      if (!profile) {
        setError(`Row ${i + 1}: Unknown stock code "${row.section}".`);
        return;
      }

      if (!sectionMap.has(row.section)) {
        sectionMap.set(row.section, { profile, rows: [] });
      }
      sectionMap.get(row.section)!.rows.push(row);
    }

    if (unassigned.length > 0) {
      setError(
        `Row${unassigned.length > 1 ? "s" : ""} ${unassigned.join(", ")} ${unassigned.length > 1 ? "have" : "has"} no section assigned.`
      );
      return;
    }

    if (sectionMap.size === 0) {
      setError("Enter at least one valid piece with a section assigned.");
      return;
    }

    // Run FFD per section
    const sections: SectionResult[] = [];

    for (const [code, { profile, rows }] of Array.from(sectionMap.entries())) {
      const sLen = profile.stockLength;
      // Use kerf 0 for Allthread, otherwise use the global kerf
      const sectionKerf = profile.category === "Allthread" ? 0 : kerfVal;

      const pieces: CutPiece[] = [];
      const cutDefs: CutDef[] = [];

      for (const row of rows) {
        const l = parseFloat(row.length) || 0;
        const rawQ = parseInt(row.qty) || 0;
        if (l <= 0 || rawQ <= 0) continue;
        if (l > sLen) {
          setError(
            `Cut length ${l}mm exceeds stock length ${sLen}mm for ${profile.label}.`
          );
          return;
        }
        const q = rawQ * mult; // Apply multiplier to quantities
        // Consolidate same-length entries
        const existing = cutDefs.find(d => d.len === l);
        if (existing) {
          existing.qty += q;
        } else {
          cutDefs.push({ len: l, qty: q });
        }
        const defIdx = cutDefs.findIndex(d => d.len === l);
        for (let j = 0; j < q; j++) pieces.push({ len: l, defIdx });
      }

      if (pieces.length === 0) continue;

      const stocks = firstFitDecreasing(pieces, sLen, sectionKerf);
      if (!stocks) {
        setError(`A piece exceeds the stock length for ${profile.label}.`);
        return;
      }

      const totalPiece = pieces.reduce((a, p) => a + p.len, 0);
      const totalStock = stocks.length * sLen;
      const totalKerfs = stocks.reduce(
        (a, s) => a + s.cuts.length * sectionKerf,
        0
      );
      const totalWaste = totalStock - totalPiece - totalKerfs;
      const yieldPct = (totalPiece / totalStock) * 100;

      sections.push({
        profileName: profile.label,
        profileCode: code,
        category: profile.category,
        stockLen: sLen,
        kerf: sectionKerf,
        stocks,
        cutDefs,
        totalPiece,
        totalStock,
        totalKerfs,
        totalWaste,
        yieldPct,
      });
    }

    const grandTotalLengths = sections.reduce((a, s) => a + s.stocks.length, 0);
    const grandTotalWaste = sections.reduce((a, s) => a + s.totalWaste, 0);
    const grandTotalStock = sections.reduce((a, s) => a + s.totalStock, 0);
    const grandTotalPiece = sections.reduce((a, s) => a + s.totalPiece, 0);
    const grandAvgUtilisation =
      grandTotalStock > 0 ? (grandTotalPiece / grandTotalStock) * 100 : 0;

    setMultiResults({
      sections,
      kerf: kerfVal,
      multiplier: mult,
      grandTotalLengths,
      grandTotalWaste,
      grandAvgUtilisation,
    });
    setResults(null);
  }, [multiCuts, kerf, multiplier]);

  const calculate = mode === "standard" ? calculateStandard : calculateMulti;

  // ── Clear All ──────────────────────────────────────────────────────────────

  const clearAll = () => {
    if (mode === "standard") {
      setCuts([{ id: 1, length: "", qty: "1" }]);
      setNextId(2);
      setResults(null);
    } else {
      setMultiCuts([{ id: 1, section: lastSection, length: "", qty: "1" }]);
      setMultiNextId(2);
      setMultiResults(null);
    }
    setError("");
    setProjectNumber("");
    setProjectName("");
    setJobName("");
    setProjectAddress("");
    setClient("");
    setNotes("");
    setJobRef("");
    setRevision("");
    setCreatedBy("");
    setMatDesc("");
    setProfileCode("");
    setStockLen("6000");
    setKerf("3");
    setMultiplier("1");
  };

  // ── Mode Switch ────────────────────────────────────────────────────────────

  const handleModeSwitch = (newMode: Mode) => {
    if (newMode === mode) return;
    // Check if there's data that would be lost
    const hasStandardData =
      mode === "standard" &&
      cuts.some(r => r.length && parseFloat(r.length) > 0);
    const hasMultiData =
      mode === "multi" &&
      multiCuts.some(r => r.length && parseFloat(r.length) > 0);

    if (
      (hasStandardData || hasMultiData) &&
      !window.confirm(
        "Switching modes will clear your current cut list. Continue?"
      )
    ) {
      return;
    }

    setMode(newMode);
    setResults(null);
    setMultiResults(null);
    setError("");
  };

  // ── PDF Export ─────────────────────────────────────────────────────────────

  const exportPdf = async () => {
    if (!results && !multiResults) {
      alert("Calculate the yield first, then export.");
      return;
    }
    setExporting(true);
    try {
      await exportCtlPdf({
        mode,
        results,
        multiResults,
        projectNumber,
        projectName,
        jobName,
        projectAddress,
        client,
        notes,
        jobRef,
        jobDate,
        revision,
        createdBy,
        multiplier,
      });
    } catch (err) {
      console.error("PDF export error:", err);
      setError("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const selectedProfile = PROFILES.find(p => p.code === profileCode);
  const uniqueLens = results
    ? Array.from(new Set(results.cutDefs.map(d => d.len)))
    : [];
  const yieldVariant = results
    ? ((results.yieldPct >= 90
        ? "good"
        : results.yieldPct >= 75
          ? "warn"
          : "bad") as "good" | "warn" | "bad")
    : "default";
  const hasResults = mode === "standard" ? !!results : !!multiResults;

  // Compact controls for the multi-profile cut list rows
  const smallControl: React.CSSProperties = {
    height: 34,
    fontSize: 12,
    padding: "0 8px",
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 940 }}>
      <div className="bsp-page-header">
        <div className="bsp-page-icon">
          <Scissors size={18} />
        </div>
        <h1 className="bsp-page-title">Cut To Length Optimiser</h1>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary"
            onClick={clearAll}
          >
            <Trash2 size={14} />
            Clear
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={exportPdf}
            disabled={exporting || !hasResults}
          >
            <Download size={15} />
            {exporting ? "Exporting…" : "Export PDF"}
          </button>
        </div>
      </div>
      <p
        style={{
          marginTop: -14,
          marginBottom: 18,
          fontSize: 13,
          color: "var(--bsp-fg-muted)",
        }}
      >
        Cutting yield optimiser with kerf-aware calculations and PDF export.
      </p>

      {/* ── Instructions (collapsible, always visible) ── */}
      <InstructionsPanel
        description="Optimise cutting yields for steel profiles. The tool uses a First Fit Decreasing algorithm with kerf allowance to minimise waste from standard stock lengths."
        steps={[
          {
            text: "Select a profile from the dropdown, or choose 'Custom' and enter your own stock length.",
          },
          {
            text: "Set the kerf (blade width) — typically 3mm for a cold saw.",
            sub: "Set the multiplier if you need multiple identical assemblies.",
          },
          { text: "Enter your cut lengths and quantities in the cut list." },
          { text: "Click Calculate Best Yield to run the optimisation." },
          {
            text: "Review the bar layout diagrams and summary, then Export PDF.",
          },
        ]}
        tips={[
          "Switch to Multi-Profile mode to optimise several different profiles in one session.",
          "Upload a CSV file to populate the cut list quickly — columns: Length, Qty (standard) or Section, Length, Qty (multi-profile).",
          "The multiplier scales all quantities before optimisation — use it for repeated frames or assemblies.",
        ]}
      />

      {/* ── Project Details ── */}
      <Card title="Project details" style={{ marginBottom: 16 }}>
        {/* Row 1: Primary fields */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "120px 1fr 1fr 140px",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div>
            <Label>Project No.</Label>
            <input
              className="bsp-input"
              value={projectNumber}
              onChange={e => setProjectNumber(e.target.value)}
              placeholder="e.g. 2638"
            />
          </div>
          <div>
            <Label>Project Name</Label>
            <input
              className="bsp-input"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              placeholder="e.g. Truganina Warehouse"
            />
          </div>
          <div>
            <Label>Job Name</Label>
            <input
              className="bsp-input"
              value={jobName}
              onChange={e => setJobName(e.target.value)}
              placeholder="e.g. Lvl 3 Mech Services"
            />
          </div>
          <div>
            <Label>Client</Label>
            <input
              className="bsp-input"
              value={client}
              onChange={e => setClient(e.target.value)}
              placeholder="e.g. SKS"
            />
          </div>
        </div>
        {/* Row 2: Secondary fields */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 120px 80px 140px",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div>
            <Label>Project Address</Label>
            <input
              className="bsp-input"
              value={projectAddress}
              onChange={e => setProjectAddress(e.target.value)}
              placeholder="e.g. 411 Palmers Rd, Truganina"
            />
          </div>
          <div>
            <Label>Drawing / Reference</Label>
            <input
              className="bsp-input"
              value={jobRef}
              onChange={e => setJobRef(e.target.value)}
              placeholder="e.g. VMM00068-Rev C"
            />
          </div>
          <div>
            <Label>Date</Label>
            <input
              className="bsp-input"
              type="date"
              value={jobDate}
              onChange={e => setJobDate(e.target.value)}
            />
          </div>
          <div>
            <Label>Revision</Label>
            <input
              className="bsp-input"
              value={revision}
              onChange={e => setRevision(e.target.value)}
              placeholder="A"
            />
          </div>
          <div>
            <Label>Created By</Label>
            <input
              className="bsp-input"
              value={createdBy}
              onChange={e => setCreatedBy(e.target.value)}
              placeholder="Name"
            />
          </div>
        </div>
        {/* Row 3: Notes */}
        <div>
          <Label>Notes</Label>
          <textarea
            className="bsp-input"
            style={{
              height: 56,
              padding: "8px 10px",
              resize: "vertical" as const,
              lineHeight: 1.4,
            }}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. LIST FOR 1 X FRAME / TOTAL 266 FRAMES"
          />
        </div>
      </Card>

      {/* ── Two-column layout ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            mode === "multi" || isMobile ? "1fr" : "360px 1fr",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        {/* LEFT (or full width in multi mode) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Profile Selection (Standard mode only) */}
          {mode === "standard" && (
            <Card title="Profile Selection">
              <div>
                <Label>Select Profile</Label>
                <select
                  className="bsp-select"
                  value={profileCode}
                  onChange={e => onProfileChange(e.target.value)}
                >
                  <option value="">— Custom / other material —</option>
                  {CATEGORIES.map(cat => (
                    <optgroup key={cat} label={cat}>
                      {PROFILES.filter(p => p.category === cat).map(p => (
                        <option key={p.code} value={p.code}>
                          {p.label} ({p.code})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              {selectedProfile && (
                <div
                  style={{
                    background: "var(--bsp-blue-tint)",
                    border: "1px solid var(--bsp-blue-border)",
                    borderRadius: 6,
                    padding: "10px 14px",
                    marginTop: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <ProfileIcon category={selectedProfile.category} />
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--bsp-blue)",
                        fontWeight: 700,
                      }}
                    >
                      {selectedProfile.label}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--bsp-blue-2)",
                        marginTop: 2,
                      }}
                    >
                      Code: {selectedProfile.code} · Stock:{" "}
                      {selectedProfile.stockLength}mm
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Stock Material & Parameters */}
          <Card title={mode === "standard" ? "Stock material" : "Parameters"}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : mode === "multi"
                    ? "1fr 1fr 1fr"
                    : "1fr 1fr",
                gap: 10,
              }}
            >
              {mode === "standard" && (
                <div>
                  <Label>Stock length</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      className="bsp-input"
                      style={{ flex: 1 }}
                      type="number"
                      value={stockLen}
                      onChange={e => setStockLen(e.target.value)}
                      min={1}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--bsp-fg-subtle)",
                        fontWeight: 700,
                      }}
                    >
                      mm
                    </span>
                  </div>
                </div>
              )}
              <div>
                <Label>Kerf / blade</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    className="bsp-input"
                    style={{ flex: 1 }}
                    type="number"
                    value={kerf}
                    onChange={e => setKerf(e.target.value)}
                    min={0}
                    step={0.5}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--bsp-fg-subtle)",
                      fontWeight: 700,
                    }}
                  >
                    mm
                  </span>
                </div>
              </div>
              <div>
                <Label>Multiplier</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    className="bsp-input"
                    style={{ flex: 1 }}
                    type="number"
                    value={multiplier}
                    onChange={e => setMultiplier(e.target.value)}
                    min={1}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--bsp-fg-subtle)",
                      fontWeight: 700,
                    }}
                  >
                    units
                  </span>
                </div>
              </div>
            </div>
            {mode === "standard" && (
              <div style={{ marginTop: 8 }}>
                <Label>Description</Label>
                <input
                  className="bsp-input"
                  value={matDesc}
                  onChange={e => setMatDesc(e.target.value)}
                  placeholder="e.g. Mega Beam 80×80 — Galv"
                />
              </div>
            )}
            {(parseInt(multiplier) || 1) > 1 && (
              <div
                style={{
                  background: "var(--bsp-blue-tint)",
                  border: "1px solid var(--bsp-blue-border)",
                  borderRadius: 4,
                  padding: "8px 12px",
                  marginTop: 8,
                  fontSize: 12,
                  color: "var(--bsp-blue)",
                  fontWeight: 600,
                }}
              >
                All quantities multiplied by ×{parseInt(multiplier) || 1}.
                Results reflect the full multiplied totals.
              </div>
            )}
          </Card>

          {/* Cut List */}
          <Card title="Cut list">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 14,
              }}
            >
              {/* Mode toggle */}
              <SegmentedToggle<Mode>
                items={[
                  { id: "standard", label: "Standard" },
                  { id: "multi", label: "Multi-Profile" },
                ]}
                active={mode}
                onChange={handleModeSwitch}
                aria-label="Cut list mode"
              />
              <div
                ref={tooltipRef}
                style={{ position: "relative", display: "inline-block" }}
              >
                <button
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCsvTooltip(prev => !prev);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    border: "1px solid var(--bsp-border-strong)",
                    background: "var(--bsp-surface)",
                    color: "var(--bsp-fg-subtle)",
                    cursor: "pointer",
                    padding: 0,
                  }}
                  title="CSV format help"
                >
                  <Info size={12} />
                </button>
                {showCsvTooltip && (
                  <div
                    style={{
                      position: "absolute",
                      top: 28,
                      right: 0,
                      zIndex: 100,
                      background: "var(--bsp-surface)",
                      border: "1px solid var(--bsp-border)",
                      borderRadius: 6,
                      padding: "12px 14px",
                      boxShadow: "var(--bsp-shadow-pop)",
                      minWidth: 240,
                      fontSize: 11,
                      color: "var(--bsp-fg)",
                    }}
                  >
                    <div
                      className="bsp-eyebrow"
                      style={{ fontSize: 11, marginBottom: 6 }}
                    >
                      CSV Format
                    </div>
                    {mode === "standard" ? (
                      <>
                        <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
                          Two columns with headers:
                        </div>
                        <div
                          className="bsp-mono"
                          style={{
                            background: "var(--bsp-blue-pale)",
                            borderRadius: 4,
                            padding: "8px 10px",
                            fontSize: 11,
                            lineHeight: 1.6,
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              color: "var(--bsp-blue)",
                              fontWeight: 700,
                            }}
                          >
                            Length,Qty
                          </div>
                          <div>1200,4</div>
                          <div>850,6</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
                          Three columns with headers. Section uses{" "}
                          <strong>stock codes</strong>:
                        </div>
                        <div
                          className="bsp-mono"
                          style={{
                            background: "var(--bsp-blue-pale)",
                            borderRadius: 4,
                            padding: "8px 10px",
                            fontSize: 11,
                            lineHeight: 1.6,
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              color: "var(--bsp-blue)",
                              fontWeight: 700,
                            }}
                          >
                            Section,Length,Qty
                          </div>
                          <div>19D1000THDG,1200,4</div>
                          <div>19D3300THDG,850,6</div>
                        </div>
                      </>
                    )}
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--bsp-fg-muted)",
                        lineHeight: 1.4,
                      }}
                    >
                      Lengths in mm. Headers are case-insensitive.
                    </div>
                  </div>
                )}
              </div>
              <label
                htmlFor="csv-upload-input"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                  letterSpacing: "0.06em",
                  border: "1px solid var(--bsp-border-strong)",
                  background: "var(--bsp-surface)",
                  color: "var(--bsp-blue)",
                  whiteSpace: "nowrap" as const,
                  textTransform: "uppercase" as const,
                }}
              >
                <Upload size={11} />
                Upload CSV
              </label>
              <input
                id="csv-upload-input"
                ref={csvInputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleCsvUpload}
              />
            </div>

            {/* Standard mode cut list */}
            {mode === "standard" && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 72px 34px",
                    gap: 8,
                    marginBottom: 6,
                    padding: "0 2px",
                  }}
                >
                  {["Length (mm)", "Qty", ""].map((h, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 10,
                        color: "var(--bsp-fg-muted)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {h}
                    </span>
                  ))}
                </div>
                {cuts.map(row => (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 72px 34px",
                      gap: 8,
                      marginBottom: 6,
                      alignItems: "center",
                    }}
                  >
                    <input
                      className="bsp-input"
                      type="number"
                      value={row.length}
                      onChange={e => updateRow(row.id, "length", e.target.value)}
                      placeholder="0"
                      min={1}
                    />
                    <input
                      className="bsp-input"
                      type="number"
                      value={row.qty}
                      onChange={e => updateRow(row.id, "qty", e.target.value)}
                      placeholder="1"
                      min={1}
                    />
                    <button
                      onClick={() => removeRow(row.id)}
                      aria-label="Remove row"
                      style={{
                        width: 34,
                        height: 38,
                        border: "1px solid var(--bsp-border-strong)",
                        borderRadius: 4,
                        background: "var(--bsp-surface)",
                        color: "var(--bsp-fg-subtle)",
                        fontSize: 17,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={addRow}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    height: 36,
                    padding: "0 12px",
                    border: "1.5px dashed var(--bsp-border-strong)",
                    borderRadius: 4,
                    background: "transparent",
                    color: "var(--bsp-fg-muted)",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    width: "100%",
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add piece
                </button>
              </>
            )}

            {/* Multi-profile mode cut list */}
            {mode === "multi" && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 100px 72px 34px",
                    gap: 8,
                    marginBottom: 6,
                    padding: "0 2px",
                  }}
                >
                  {["Section", "Length (mm)", "Qty", ""].map((h, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 10,
                        color: "var(--bsp-fg-muted)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {h}
                    </span>
                  ))}
                </div>
                {multiCuts.map(row => (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 100px 72px 34px",
                      gap: 8,
                      marginBottom: 6,
                      alignItems: "center",
                    }}
                  >
                    <select
                      className="bsp-select"
                      style={{
                        ...smallControl,
                        borderColor:
                          row.section === "" && row.length
                            ? "var(--bsp-red)"
                            : undefined,
                      }}
                      value={row.section}
                      onChange={e =>
                        updateMultiRow(row.id, "section", e.target.value)
                      }
                    >
                      <option value="">— Select section —</option>
                      {CATEGORIES.map(cat => (
                        <optgroup key={cat} label={cat}>
                          {PROFILES.filter(p => p.category === cat).map(p => (
                            <option key={p.code} value={p.code}>
                              {p.label} ({p.code})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <input
                      className="bsp-input"
                      style={smallControl}
                      type="number"
                      value={row.length}
                      onChange={e =>
                        updateMultiRow(row.id, "length", e.target.value)
                      }
                      placeholder="0"
                      min={1}
                    />
                    <input
                      className="bsp-input"
                      style={smallControl}
                      type="number"
                      value={row.qty}
                      onChange={e =>
                        updateMultiRow(row.id, "qty", e.target.value)
                      }
                      placeholder="1"
                      min={1}
                    />
                    <button
                      onClick={() => removeMultiRow(row.id)}
                      aria-label="Remove row"
                      style={{
                        width: 34,
                        height: 34,
                        border: "1px solid var(--bsp-border-strong)",
                        borderRadius: 4,
                        background: "var(--bsp-surface)",
                        color: "var(--bsp-fg-subtle)",
                        fontSize: 17,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={addMultiRow}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    height: 36,
                    padding: "0 12px",
                    border: "1.5px dashed var(--bsp-border-strong)",
                    borderRadius: 4,
                    background: "transparent",
                    color: "var(--bsp-fg-muted)",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    width: "100%",
                    marginTop: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add piece
                </button>
              </>
            )}
          </Card>

          {error && (
            <div
              style={{
                background: "var(--bsp-red-tint)",
                border: "1px solid var(--bsp-red)",
                borderRadius: 6,
                color: "var(--bsp-red-fg)",
                fontSize: 13,
                padding: "10px 14px",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={calculate}
            style={{ width: "100%", height: 48 }}
          >
            <Zap size={15} />
            Calculate best yield
          </button>
        </div>

        {/* RIGHT column */}
        {mode === "standard" && results && (
          <div
            ref={resultsRef}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {/* Summary */}
            <Card title="Summary">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(3, 1fr)",
                  gap: 8,
                  marginBottom: "1rem",
                }}
              >
                <MetricCard
                  label="Lengths needed"
                  value={String(results.stocks.length)}
                />
                <MetricCard
                  label="Yield"
                  value={`${results.yieldPct.toFixed(1)}%`}
                  variant={yieldVariant}
                />
                <MetricCard
                  label="Total waste"
                  value={`${results.totalWaste.toFixed(0)}mm`}
                />
                <MetricCard
                  label="Kerf loss"
                  value={`${results.totalKerfs.toFixed(0)}mm`}
                />
                <MetricCard
                  label="Total req."
                  value={`${(results.totalStock / 1000).toFixed(2)}m`}
                />
                <MetricCard
                  label="Pieces cut"
                  value={String(
                    results.stocks.reduce((a, s) => a + s.cuts.length, 0)
                  )}
                />
              </div>
              <table className="bsp-table">
                <thead>
                  <tr>
                    {["Size", "Qty", "Total mm"].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.cutDefs.map((d, i) => (
                    <tr key={i}>
                      <td className="bsp-mono">
                        {d.len}mm
                        {results.matDesc ? ` — ${results.matDesc}` : ""}
                      </td>
                      <td className="bsp-mono">{d.qty}</td>
                      <td className="bsp-mono">{d.len * d.qty}</td>
                    </tr>
                  ))}
                  <tr style={{ background: "var(--bsp-blue-tint)" }}>
                    <td
                      className="bsp-mono"
                      style={{ color: "var(--bsp-blue)", fontWeight: 700 }}
                    >
                      TOTAL
                      {results.profileLabel ? ` — ${results.profileLabel}` : ""}
                    </td>
                    <td
                      className="bsp-mono"
                      style={{ color: "var(--bsp-blue)", fontWeight: 700 }}
                    >
                      {results.stocks.reduce((a, s) => a + s.cuts.length, 0)}
                    </td>
                    <td
                      className="bsp-mono"
                      style={{ color: "var(--bsp-blue)", fontWeight: 700 }}
                    >
                      {results.totalPiece}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Multiplier indicator */}
              {(parseInt(multiplier) || 1) > 1 && (
                <div
                  style={{
                    marginTop: 8,
                    background: "var(--bsp-blue-tint)",
                    border: "1px solid var(--bsp-blue-border)",
                    borderRadius: 4,
                    padding: "8px 12px",
                    fontSize: 12,
                    color: "var(--bsp-blue)",
                    fontWeight: 600,
                  }}
                >
                  Results include ×{parseInt(multiplier) || 1} multiplier
                  applied to all quantities.
                </div>
              )}
            </Card>

            {/* Bar layout */}
            <Card title="Length layout — optimised cut sequence">
              <div data-bar-layout>
                <BarLayout
                  stocks={results.stocks}
                  stockLen={results.stockLen}
                  kerf={results.kerf}
                  uniqueLens={uniqueLens}
                />
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* ── Multi-Profile Results (full width below input) ── */}
      {mode === "multi" && multiResults && (
        <div ref={resultsRef} style={{ marginTop: "1rem" }}>
          {/* Per-section results */}
          {multiResults.sections.map((sec, si) => {
            const secUniqueLens = Array.from(
              new Set(sec.cutDefs.map(d => d.len))
            );
            const secYieldVariant =
              sec.yieldPct >= 90 ? "good" : sec.yieldPct >= 75 ? "warn" : "bad";

            return (
              <div key={si} style={{ marginBottom: "1.5rem" }}>
                {/* Section header */}
                <div
                  style={{
                    background: "var(--bsp-blue)",
                    borderRadius: "8px 8px 0 0",
                    padding: "10px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      background: "rgba(255,255,255,0.15)",
                      borderRadius: 6,
                      padding: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ProfileIcon category={sec.category} size={28} />
                  </div>
                  <div>
                    <div
                      style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}
                    >
                      {sec.profileName}
                    </div>
                    <div
                      style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}
                    >
                      Code: {sec.profileCode} · Stock: {sec.stockLen}mm · Kerf:{" "}
                      {sec.kerf}mm
                    </div>
                  </div>
                </div>

                {/* Section summary */}
                <Card
                  style={{
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    marginBottom: 0,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "repeat(2, 1fr)"
                        : "repeat(4, 1fr)",
                      gap: 8,
                      marginBottom: "1rem",
                    }}
                  >
                    <MetricCard
                      label="Lengths needed"
                      value={String(sec.stocks.length)}
                    />
                    <MetricCard
                      label="Yield"
                      value={`${sec.yieldPct.toFixed(1)}%`}
                      variant={secYieldVariant as "good" | "warn" | "bad"}
                    />
                    <MetricCard
                      label="Total waste"
                      value={`${sec.totalWaste.toFixed(0)}mm`}
                    />
                    <MetricCard
                      label="Pieces cut"
                      value={String(
                        sec.stocks.reduce((a, s) => a + s.cuts.length, 0)
                      )}
                    />
                  </div>

                  {/* Cut schedule */}
                  <table className="bsp-table" style={{ marginBottom: 16 }}>
                    <thead>
                      <tr>
                        {["Size", "Qty", "Total mm"].map(h => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sec.cutDefs.map((d, i) => (
                        <tr key={i}>
                          <td className="bsp-mono">{d.len}mm</td>
                          <td className="bsp-mono">{d.qty}</td>
                          <td className="bsp-mono">{d.len * d.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Bar layout */}
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--bsp-blue)",
                      marginBottom: 8,
                    }}
                  >
                    Length layout
                  </div>
                  <BarLayout
                    stocks={sec.stocks}
                    stockLen={sec.stockLen}
                    kerf={sec.kerf}
                    uniqueLens={secUniqueLens}
                  />
                </Card>
              </div>
            );
          })}

          {/* Material Requirements Summary */}
          <Card
            title="Material requirements summary"
            actions={
              multiResults.multiplier > 1 ? (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--bsp-blue)",
                    background: "var(--bsp-blue-tint)",
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  ×{multiResults.multiplier} multiplier applied
                </span>
              ) : undefined
            }
          >
            <MaterialSummaryTable sections={multiResults.sections} />
          </Card>
        </div>
      )}
    </div>
  );
}
